import { translate } from "../../i18n/catalog.js";
import { wikiFixedLabelKeys } from "../../i18n/wiki/index.js";
import { brandDisplayLabel } from "../../i18n/brand/index.js";
import { normalizeWikiCategoryLabel } from "../../utils/wikiCategoryLabels.js";

const categories = Object.freeze({
  存储器: "memory",
  儲存器: "memory",
  memory: "memory",
  闪存: "flash",
  flash: "flash",
  "Flash memory": "flash",
  "NAND 闪存": "nand",
  NAND闪存: "nand",
  "NAND Flash": "nand",
  "NOR 闪存": "nor",
  NOR闪存: "nor",
  "NOR Flash": "nor",
  固态硬盘: "ssd",
  芯片: "chip",
  百科: "wiki",
  微控制器: "mcu",
  Microcontrollers: "mcu",
  电源管理芯片: "power",
  "Power Management ICs": "power",
  接口芯片: "interface",
  "Interface ICs": "interface",
  传感器: "sensor",
  Sensors: "sensor",
});
const fields = Object.freeze({
  Capacity: "parameter.capacity",
  Density: "parameter.density",
  存储容量: "parameter.capacity",
  儲存容量: "parameter.capacity",
  Package: "parameter.package",
  封装类型: "parameter.package",
  封装形式: "parameter.package",
  PackageType: "parameter.package",
  Voltage: "parameter.voltage",
  工作电压: "parameter.operatingVoltage",
  "Supply Voltage": "parameter.supplyVoltage",
  供电电压: "parameter.supplyVoltage",
  Speed: "parameter.speed",
  速度: "parameter.speed",
  访问时间: "parameter.accessTime",
  "Access Time": "parameter.accessTime",
  Frequency: "parameter.frequency",
  "Operating Temperature": "parameter.temperature",
  Interface: "parameter.interface",
  "Bus Width": "parameter.width",
  "Pin Count": "parameter.pins",
  品牌: "parameter.brand",
  厂商: "parameter.manufacturer",
  产品分类: "parameter.category",
  型号名称: "parameter.model",
  参数名称: "parameter.parameter",
  厂家: "parameter.manufacturer",
  数据位宽: "parameter.width",
  组织结构: "parameter.organization",
});
const states = Object.freeze({
  draft: "draft",
  pending_review: "reviewing",
  reviewing: "processing",
  manual_review: "manual",
  ai_correction_pending: "confirm",
  revision_required: "revision",
  rejected: "rejected",
  denied: "denied",
  published: "published",
  offline: "offline",
  scheduled: "scheduled",
});
export function wikiCategoryLabel(value, locale) {
  const raw = String(value ?? "");
  // 保留来源类别用于查询，仅将已登记的全称投影为通用技术缩写。
  const scientificNames = {
    "动态随机存储器(DRAM)": "DRAM",
    "静态随机存储器(SRAM)": "SRAM",
  };
  if (Object.hasOwn(scientificNames, raw)) return scientificNames[raw];
  const normalized = normalizeWikiCategoryLabel(raw);
  const key = Object.hasOwn(categories, raw)
    ? categories[raw]
    : Object.hasOwn(categories, normalized)
    ? categories[normalized]
    : null;
  // 不将显示名写回 normalizeWikiCategoryLabel，它仍用于过滤、缓存和查询别名。
  return key
    ? translate(locale, `wikiDisplay.category.${key}`)
    : /^(DRAM|SRAM|SSD|EEPROM|PROM|EPROM|FIFO|NVRAM)$/u.test(normalized)
    ? normalized
    : raw;
}
export function wikiFieldLabel(value, locale) {
  const raw = String(value ?? "");
  const key = Object.hasOwn(fields, raw)
    ? `wikiDisplay.${fields[raw]}`
    : Object.hasOwn(wikiFixedLabelKeys, raw)
    ? wikiFixedLabelKeys[raw]
    : null;
  return key
    ? locale === "zh-CN" && /[\u3400-\u9fff]/u.test(raw)
      ? raw
      : translate(locale, key)
    : raw;
}
export function wikiStatusLabel(value, locale) {
  const raw = String(value ?? "");
  return Object.hasOwn(states, raw)
    ? translate(locale, `wikiDisplay.status.${states[raw]}`)
    : wikiFieldLabel(raw, locale);
}
export function wikiFieldValueLabel(label, value, locale) {
  const rawLabel = String(label ?? "");
  if (["所属分类", "产品分类", "Category"].includes(rawLabel))
    return wikiCategoryLabel(value, locale);
  if (
    [
      "品牌厂商",
      "厂家",
      "厂商",
      "制造商",
      "Manufacturer",
      "Brand / manufacturer",
    ].includes(rawLabel)
  )
    return wikiManufacturerLabel(value, locale);
  if (["状态", "审核状态", "Status"].includes(rawLabel))
    return wikiStatusLabel(value, locale);
  return value;
}
export function wikiManufacturerLabel(value, locale) {
  if (!value || typeof value === "string")
    return brandDisplayLabel(value, locale);
  const raw = value.raw || value;
  const original =
    value.manufacturer_zh ||
    raw.manufacturer_zh ||
    value.brand ||
    raw.brand ||
    value.manufacturer ||
    raw.manufacturer ||
    "";
  const en =
    value.manufacturer_en ||
    raw.manufacturer_en ||
    raw.manufacturer ||
    value.manufacturer ||
    "";
  // 英文来源若本身是中文，仍用已登记品牌别名；不把它冒充英文译名。
  return brandDisplayLabel(
    { name: original, nameEn: /[\u3400-\u9fff]/u.test(en) ? "" : en },
    locale
  );
}
export function wikiCardParameterText(parameter, locale) {
  if (
    !parameter ||
    parameter.value === undefined ||
    parameter.value === null ||
    parameter.value === ""
  )
    return "";
  return translate(locale, "wikiDisplay.parameterText", {
    label: wikiFieldLabel(parameter.label || parameter.name || "", locale),
    value: parameter.value,
  });
}
const errors = Object.freeze({
  AUTH_REQUIRED: "AUTH_REQUIRED",
  UNAUTHORIZED: "AUTH_REQUIRED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  INVALID_ARGUMENT: "INVALID_ARGUMENT",
  VALIDATION_ERROR: "INVALID_ARGUMENT",
  RATE_LIMITED: "RATE_LIMITED",
  SERVICE_UNAVAILABLE: "UNAVAILABLE",
  TIMEOUT: "TIMEOUT",
});
const legacyErrors = Object.freeze({
  请先登录: "AUTH_REQUIRED",
  请先登录后再操作: "AUTH_REQUIRED",
  未登录: "AUTH_REQUIRED",
  没有审核权限: "FORBIDDEN",
  当前账号没有百科审核权限: "FORBIDDEN",
});
export function wikiErrorMessage(
  error,
  locale,
  fallbackKey = "wikiDisplay.error.REQUEST_FAILED"
) {
  const data = error?.response?.data || {};
  const detail = data.detail;
  const rawCode =
    data.errorCode ||
    data.error_code ||
    (typeof detail === "object" ? detail?.error_code || detail?.code : "") ||
    "";
  const legacy = typeof detail === "string" ? detail : detail?.message;
  const status = Number(error?.response?.status);
  const key =
    (Object.hasOwn(errors, rawCode) ? errors[rawCode] : null) ||
    (Object.hasOwn(legacyErrors, legacy) ? legacyErrors[legacy] : null) ||
    (status === 401
      ? "AUTH_REQUIRED"
      : status === 403
      ? "FORBIDDEN"
      : status === 404
      ? "NOT_FOUND"
      : status === 429
      ? "RATE_LIMITED"
      : status >= 500
      ? "UNAVAILABLE"
      : ["ECONNABORTED", "ETIMEDOUT"].includes(error?.code)
      ? "TIMEOUT"
      : error?.code === "ERR_NETWORK"
      ? "UNAVAILABLE"
      : null);
  // 未知 detail 可能包含内部诊断或用户原文：不用字符串替换猜译，也不作为固定错误暴露。
  return translate(locale, key ? `wikiDisplay.error.${key}` : fallbackKey);
}
